import type {
  MaterialNodeSpec,
  OperationNodeSpec,
  TransferOptions,
  Duration,
  Checkpoint,
} from '../../graph/domain/types';
import type { WorkflowGraph } from '../../graph';
import type { Store } from '../types';
import type { RecommendedInstrumentRef } from '../../components/modals/AddStepModal/types';
import { propagateTransferComposition } from '../compositionService';
import {
  type TransferRowFormState,
  type NewDestinationDraft,
  buildSourcePostState,
  buildDestinationPostState,
  quantityTypeFromUnit,
} from './transferHelpers';

type SetState = (partial: Partial<Store> | ((s: Store) => Partial<Store>)) => void;
type GetState = () => Store;
type GetGraph = () => WorkflowGraph | null | undefined;

// ── Public types ────────────────────────────────────────────────────────────

export interface AddTransferStepInput {
  sourceMaterialId: string;
  operationTypeId: string;
  operationDisplayName: string;
  dataExpected?: boolean;
  row: TransferRowFormState;
  transferOptions: TransferOptions;
  destinationMaterialId?: string;
  destinationDraft?: NewDestinationDraft;
  duration?: Duration;
  notes?: string;
  comments?: string;
  recommendedInstrument?: RecommendedInstrumentRef[];
  checkpoints?: Checkpoint[];
  operationTemplate?: {
    operationTypeId: string;
    parameters?: Array<{ parameterName?: string; value?: unknown; unit?: string; fieldType?: string; required?: boolean; tags?: string[] }>;
    hideParamsMap?: Record<string, boolean>;
    translationTemplate?: string;
  };
  approvers?: any;
  notifications?: any;
}

export interface AddTransferStepResult {
  operationId: string;
  sourcePostMaterialId: string;
  destinationPostMaterialId: string;
}

// ── Positioning constants ───────────────────────────────────────────────────

const SOURCE_TO_OPERATION_DY = 120;
const OPERATION_TO_POST_DY = 120;
const POST_MATERIAL_DX = 140;

// ── Factory ─────────────────────────────────────────────────────────────────

export function createTransferActions(
  set: SetState,
  get: GetState,
  getGraph: GetGraph,
) {
  return {
    addTransferStep(input: AddTransferStepInput): AddTransferStepResult {
      const source = get().graphData.materialNodes[input.sourceMaterialId];
      if (!source) {
        throw new Error(`Source material not found: ${input.sourceMaterialId}`);
      }

      // Resolve  destination
      let destinationId: string;
      let destination: MaterialNodeSpec | null;

      if (input.destinationMaterialId) {
        destination =
          get().graphData.materialNodes[input.destinationMaterialId] ?? null;
        if (!destination) {
          throw new Error(
            `Destination material not found: ${input.destinationMaterialId}`,
          );
        }
        destinationId = input.destinationMaterialId;
      } else if (input.destinationDraft) {
        destinationId = get().addMaterialNode({
          name: input.destinationDraft.name,
          container: input.destinationDraft.container,
          constituents: [],
          quantity: null,
          isEmpty: true,
        });
        destination = null; // signals: no pre-state edge
      } else {
        throw new Error(
          'Either destinationMaterialId or destinationDraft is required',
        );
      }

      // Position calculation
      const sourcePos = source.position ?? { x: 0, y: 0 };
      const operationPos = {
        x: sourcePos.x,
        y: sourcePos.y + SOURCE_TO_OPERATION_DY,
      };
      const sourcePostPos = {
        x: operationPos.x - POST_MATERIAL_DX / 2,
        y: operationPos.y + OPERATION_TO_POST_DY,
      };
      const destPostPos = {
        x: operationPos.x + POST_MATERIAL_DX / 2,
        y: operationPos.y + OPERATION_TO_POST_DY,
      };

      // Compute correct global creationOrder (max existing + 1)
      let maxOrder = 0;
      for (const op of Object.values(get().graphData.operationNodes)) {
        maxOrder = Math.max(maxOrder, op.creationOrder ?? 0);
      }
      const nextOrder = maxOrder + 1;

      // 1. Create the operation node
      const operationSpec: OperationNodeSpec = {
        archetype: 'TRANSFER',
        operationTypeId: input.operationTypeId,
        displayName: input.operationDisplayName,
        dataExpected: input.dataExpected ?? false,
        parameters: {},
        checkPoints: input.checkpoints || [],
        duration: input.duration,
        notes: input.notes,
        comments: input.comments,
        recommendedInstrument: input.recommendedInstrument,
        operationTemplate: input.operationTemplate,
        approvers: input.approvers,
        notifications: input.notifications,
        creationOrder: nextOrder,
        position: operationPos,
        transferRow: {
          id: input.row.id,
          sourceMaterialId: input.sourceMaterialId,
          destinationMaterialId: destinationId,
          quantity:
            input.row.mode === 'numeric' &&
            input.row.amount != null &&
            input.row.unit != null
              ? {
                  value: { amount: input.row.amount, units: input.row.unit },
                  type: quantityTypeFromUnit(input.row.unit),
                }
              : undefined,
          symbolic:
            input.row.mode === 'symbolic'
              ? (input.row.symbolic ?? undefined)
              : undefined,
        },
        transferOptions: input.transferOptions
          ? {
              ...input.transferOptions,
              transferEntireVolume:
                input.row.amount === 0 || (input.row.amount != null && input.row.amount <= 0)
                  ? false
                  : Boolean(input.transferOptions.transferEntireVolume),
            }
          : undefined,
      };
      const operationId = get().addOperationNode(operationSpec);

      // 2. Source post-state material
      const sourcePostSpec = buildSourcePostState(source, input.row);
      sourcePostSpec.position = sourcePostPos;

      // 3. Destination post-state material
      const destPostSpec = buildDestinationPostState(
        source,
        destination,
        input.row,
        destinationId,
      );
      destPostSpec.position = destPostPos;

      // Stamp composition lineage on both post-state materials
      const transferQuantity = operationSpec.transferRow?.quantity;
      const destNode = destination ?? get().graphData.materialNodes[destinationId];
      const { sourcePostComposition, destPostComposition } = propagateTransferComposition(
        input.sourceMaterialId,
        source.name,
        destinationId,
        destNode?.name,
        operationId,
        transferQuantity,
      );
      sourcePostSpec.composition = sourcePostComposition;
      destPostSpec.composition = destPostComposition;

      const sourcePostMaterialId = get().addMaterialNode(sourcePostSpec);
      const destinationPostMaterialId = get().addMaterialNode(destPostSpec);

      // Record the post-state material ids on the operation's transferRow so
      // the archetype data is self-describing (persisted as transferData on
      // the backend; consumed by run-stage transfer execution).
      set((s) => {
        const op = s.graphData.operationNodes[operationId];
        if (!op?.transferRow) return {};
        return {
          graphData: {
            ...s.graphData,
            operationNodes: {
              ...s.graphData.operationNodes,
              [operationId]: {
                ...op,
                transferRow: {
                  ...op.transferRow,
                  sourcePostMaterialId,
                  destinationPostMaterialId,
                },
              },
            },
          },
        };
      });

      // 4. Edges — update store state via edgeActions (which also attempts
      // graph sync via the broken half-pair mechanism; addTransferVisualEdges
      // below replaces any wrong edge with the correct 3 visual edges).
      get().connectMaterialToOperation(
        input.sourceMaterialId,
        operationId,
      );
      if (destination) {
        // Existing destination has a pre-state edge
        get().connectMaterialToOperation(destinationId, operationId);
      }
      get().connectOperationToMaterial(operationId, sourcePostMaterialId);
      get().connectOperationToMaterial(
        operationId,
        destinationPostMaterialId,
      );

      // 5. Fix visual representation: replace the single wrong edge created
      // by _pendingEdgeHalf with the correct 2-3 transfer edges.
      getGraph()?.addTransferVisualEdges({
        operationId,
        srcPre: input.sourceMaterialId,
        dstPre: destination ? destinationId : null,
        srcPost: sourcePostMaterialId,
        dstPost: destinationPostMaterialId,
        displayName: input.operationDisplayName,
      });

      return {
        operationId,
        sourcePostMaterialId,
        destinationPostMaterialId,
      };
    },
  };
}
